import { Timestamp } from "firebase/firestore";
import { APP_ID } from "./constants";

export const previewUsers = [
  { uid: "preview-admin", name: "預覽管理員", role: "admin" },
  { uid: "preview-employee", name: "預覽正職", role: "fulltime" },
  { uid: "preview-proxy", name: "預覽代理人", role: "fulltime" },
  { uid: "preview-parttime", name: "預覽工讀生", role: "parttime" },
];
export const previewDb = { localPreview: true };
const root = `artifacts/${APP_ID}`;
const records = new Map();
const listeners = new Set();
const authListeners = new Set();
const storedUid = (() => {
  try { return sessionStorage.getItem("clocking-preview-user"); } catch { return null; }
})();
let currentUid = previewUsers.some(u => u.uid === storedUid) ? storedUid : previewUsers[0].uid;

const clone = value => {
  if (value instanceof Timestamp) return Timestamp.fromMillis(value.toMillis());
  if (value instanceof Date) return new Date(value);
  if (Array.isArray(value)) return value.map(clone);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([k, v]) => [k, clone(v)]));
  return value;
};
const fakeUser = uid => {
  const person = previewUsers.find(u => u.uid === uid);
  return person ? { ...person, displayName: person.name, email: `${uid}@example.com`,
    emailVerified: true, photoURL: null,
    getIdToken: async () => { throw new Error("本機預覽不提供登入憑證"); },
  } : null;
};
export const previewAuth = {
  get currentUser() { return fakeUser(currentUid); },
  signOut: async () => { currentUid = null; authListeners.forEach(cb => cb(null)); },
};
export function selectPreviewUser(uid) {
  if (!previewUsers.some(u => u.uid === uid)) throw new Error("未知預覽帳號");
  currentUid = uid;
  try { sessionStorage.setItem("clocking-preview-user", uid); } catch { /* Session storage is optional. */ }
  authListeners.forEach(cb => cb(fakeUser(uid)));
}
export function previewAuthChanged(callback) {
  authListeners.add(callback);
  queueMicrotask(() => { if (authListeners.has(callback)) callback(fakeUser(currentUid)); });
  return () => authListeners.delete(callback);
}

const today = new Date();
const dateAt = (offset, hour) => {
  const date = new Date(today);
  date.setDate(date.getDate() + offset);
  date.setHours(hour, 0, 0, 0);
  return date;
};
records.set(`${root}/public/data/settings/main`, { adminUIDs: ["preview-admin"], allowedIP: "", companyLat: null, companyLng: null });
for (const person of previewUsers) {
  const profile = { ...person, fullName: person.name, email: `${person.uid}@example.com`,
    personalEmail: `${person.uid}@example.com`, photoURL: null, accountStatus: "active",
    isProfileFilled: true, isHidden: false, canViewAllHours: person.role !== "parttime",
    annualLeaveRemaining: 7, annualLeaveUsed: 1, startDate: "2025-04-01", endDate: "",
    birthday: "1995-10-15", baseSalary: 36000, hourlyRate: 200, laborInsurance: 900,
    healthInsurance: 560, familyMembers: [], workExperience: [],
  };
  records.set(`${root}/users/${person.uid}/profile/main`, profile);
  records.set(`${root}/public/data/daily_status/${person.uid}`, { ...person, fullName: person.name,
    photoURL: null, accountStatus: "active", isHidden: false, birthday: profile.birthday, status: "out" });
  records.set(`${root}/public/data/notification_contacts/uid-${person.uid}`, { uid: person.uid, email: profile.email });
  for (const [type, hour] of [["in", 9], ["out", 18]]) {
    const entry = { uid: person.uid, userName: person.name, type, timestamp: Timestamp.fromDate(dateAt(-1, hour)), ip: "127.0.0.1", device: "preview" };
    records.set(`${root}/users/${person.uid}/logs/sample-${type}`, entry);
    records.set(`${root}/public/data/company_logs/${person.uid}-${type}`, entry);
  }
}
for (const [id, offset, status, signed] of [["pending", 2, "pending", false], ["signed", 4, "pending", true], ["approved", -7, "approved", true]]) {
  records.set(`${root}/public/data/leaves/preview-${id}`, { uid: "preview-employee", userName: "預覽正職",
    proxyUid: "preview-proxy", proxyEmail: "", proxySignature: signed ? "預覽代理人" : "",
    ...(signed ? { proxySignerUid: "preview-proxy" } : {}), managerSignature: status === "approved" ? "預覽管理員" : "",
    type: "annual", typeName: "特休", status, reason: "預覽資料", startDate: dateAt(offset, 9).toISOString(),
    endDate: dateAt(offset, 18).toISOString(), hours: 7.5, days: 1, createdAt: Timestamp.now(),
  });
}

export const previewRef = (kind, parent, parts) => ({ localPreview: true, kind,
  path: [parent.path, ...parts].filter(Boolean).join("/") || crypto.randomUUID() });
export const previewConstraint = (kind, ...args) => ({ kind, args });
const field = (value, key) => key.split(".").reduce((v, k) => v?.[k], value);
const comparable = value => value instanceof Timestamp ? value.toMillis() : value instanceof Date ? value.getTime() : value;
const snapshotDoc = ref => {
  const data = clone(records.get(ref.path));
  return { id: ref.path.split("/").pop(), ref, exists: () => data !== undefined, data: () => clone(data) };
};
export function previewSnapshot(ref) {
  if (ref.kind === "doc") return snapshotDoc(ref);
  const constraints = ref.constraints || [];
  let docs = [...records.keys()].filter(path => path.startsWith(`${ref.path}/`) && path.split("/").length === ref.path.split("/").length + 1)
    .map(path => snapshotDoc({ localPreview: true, kind: "doc", path }));
  for (const { kind, args } of constraints.filter(c => c.kind === "where")) {
    const [key, op, expected] = args;
    docs = docs.filter(d => {
      const value = field(d.data(), key);
      const a = comparable(value), b = comparable(expected);
      if (op === "==") return a === b;
      if (op === "!=") return value !== undefined && a !== b;
      if (op === ">=") return a >= b;
      if (op === "<=") return a <= b;
      if (op === ">") return a > b;
      if (op === "<") return a < b;
      if (op === "in") return expected.includes(value);
      if (op === "array-contains") return value?.includes(expected);
      throw new Error(`Unsupported preview query: ${kind} ${op}`);
    });
  }
  const ordering = constraints.filter(c => c.kind === "orderBy");
  docs = docs.filter(d => ordering.every(c => field(d.data(), c.args[0]) !== undefined));
  docs.sort((a, b) => {
    for (const { args: [key, direction] } of ordering) {
      const x = comparable(field(a.data(), key)), y = comparable(field(b.data(), key));
      if (x !== y) return (x < y ? -1 : 1) * (direction === "desc" ? -1 : 1);
    }
    return 0;
  });
  const cap = constraints.find(c => c.kind === "limit");
  if (cap) docs = docs.slice(0, cap.args[0]);
  return { docs, size: docs.length, empty: !docs.length, forEach: callback => docs.forEach(callback) };
}
export function previewSubscribe(ref, callback, errorCallback) {
  const listener = () => {
    try { callback(previewSnapshot(ref)); } catch (error) { if (errorCallback) errorCallback(error); else console.error(error); }
  };
  const item = { ref, listener };
  listeners.add(item);
  queueMicrotask(() => { if (listeners.has(item)) listener(); });
  return () => listeners.delete(item);
}
export function previewWrite(ref, data, merge = false, notify = true) {
  const previous = records.get(ref.path) || {};
  const next = merge ? clone(previous) : {};
  for (const [key, value] of Object.entries(data)) {
    if (value?.previewTransform === "delete") delete next[key];
    else if (value?.previewTransform === "union") next[key] = [...new Set([...(previous[key] || []), ...value.values])];
    else if (value?.previewTransform === "remove") next[key] = (previous[key] || []).filter(v => !value.values.includes(v));
    else next[key] = clone(value);
  }
  records.set(ref.path, next);
  if (notify) previewEmit([ref.path]);
}
export function previewEmit(paths) {
  // Only notify affected collections; profile saves must not reload every screen.
  for (const { ref, listener } of listeners) {
    if (paths.some(path => ref.kind === "doc" ? path === ref.path : path.startsWith(`${ref.path}/`))) queueMicrotask(listener);
  }
}
export function previewDelete(ref, notify = true) {
  records.delete(ref.path);
  if (notify) previewEmit([ref.path]);
}
